import React, { Component } from 'react'
import PropTypes from 'prop-types'
import PhotoWithViolations from './PhotoWithViolations'
import { getModel, updateModel } from '../../data/Model'
import NoClientsSelected from '../NoClientsSelected'
import Button from '../../generic/Button'
import { getSelectedClients } from '../../data/interactions'
import { requestAPI } from '../../data/api'
import setAction from './setAction'
import ClientCombobox from './ClientCombobox'
import { describeApplyFailure, describeViolation, summariseApplyFailures } from './applyFailures'
import { markets } from '../../lib/markets'
import './AssignedViolations.css'

class AssignedViolations extends Component {
	lockTimerInterval = null

	constructor(props) {
		super(props);
		this.state = {
			cachedActions: {}, // Map of violation_id -> action
			isApplying: false,
			availableClients: [],
			// Map of client_id -> per-channel { violations, locked, available } for the current stage;
			// null until loaded (or when loading failed), so the dropdown knows not to filter on it.
			clientCounts: null,
			selectedClientId: '',
			loadingClients: true,
			availableGroups: [],
			selectedGroup: '',
			loadingGroups: true,
			requestedAmount: 20,
			selectedUserId: '',
			users: [],
			// The queue hands out one channel at a time: 'web', 'facebook' or 'instagram'.
			channel: 'web',
			selectedMarket: '',
			createdFrom: '',
			createdTo: '',
			timeRemaining: null,
		};
	}

	startLockTimer = (lockTimestamp) => {
		if (this.lockTimerInterval) {
			clearInterval(this.lockTimerInterval)
		}
		const lockExpiresAt = new Date(new Date(lockTimestamp).getTime() + 60 * 60 * 1000)
		const updateTimer = () => {
			const remaining = lockExpiresAt - Date.now()
			if (remaining <= 0) {
				clearInterval(this.lockTimerInterval)
				this.lockTimerInterval = null
				this.setState({ timeRemaining: 0 })
				alert('Your violation lock has expired. Please fetch new violations.')
				return
			}
			this.setState({ timeRemaining: remaining })
		}
		updateTimer()
		this.lockTimerInterval = setInterval(updateTimer, 1000)
	}

	formatTimeRemaining = (ms) => {
		const totalSeconds = Math.max(0, Math.floor(ms / 1000))
		const minutes = Math.floor(totalSeconds / 60)
		const seconds = totalSeconds % 60
		return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
	}

	handleCachedActionChange = (violationId, action) => {
		this.setState(prevState => {
			const newCachedActions = { ...prevState.cachedActions };
			if (action === null) {
				delete newCachedActions[violationId];
			} else {
				newCachedActions[violationId] = action;
			}
			return { cachedActions: newCachedActions };
		});
	};

	loadAvailableGroups = async (stage) => {
		this.setState({ loadingGroups: true })
		try {
			// Get all main clients from /hubs endpoint
			const response = await requestAPI(
				`/hubs/groups`,
				'GET'
			)

			// Process the response - expecting an array of hub objects
			const availableGroups = Array.isArray(response)
				? response
				: []

			// Do not auto-select any group by default
			let selectedGroup = this.state.selectedGroup
			// Only keep current selection if it's still valid in the new group list
			if (selectedGroup && !availableGroups.find(g => g === selectedGroup)) {
				selectedGroup = ''
			}

			this.setState({
				availableGroups,
				selectedGroup,
				loadingGroups: false,
			})

			return selectedGroup
		} catch (error) {
			console.error('Failed to load available groups:', error)
			this.setState({
				availableGroups: [],
				loadingGroups: false,
			})
			return ''
		}
	}

		       // Track the current request to prevent race conditions for loadAvailableClients
		       static loadClientsRequestId = 0;

		       loadAvailableClients = async (groupNameOverride) => {
			       this.setState({ loadingClients: true })
			       AssignedViolations.loadClientsRequestId++;
			       const thisRequestId = AssignedViolations.loadClientsRequestId;
			       try {
				       // If a group is selected (not default/empty), pass groupname as a query param
				       const selectedGroup = groupNameOverride !== undefined ? groupNameOverride : this.state.selectedGroup;
				       let endpoint = '/hubs/main';
				       if (selectedGroup && selectedGroup !== '') {
					       endpoint += `?groupName=${encodeURIComponent(selectedGroup)}`;
				       }
				       const response = await requestAPI(
					       endpoint,
					       'GET'
				       )

				       // If a newer request has started, abort this one
				       if (thisRequestId !== AssignedViolations.loadClientsRequestId) {
					       return '';
				       }

				       // Process the response - expecting an array of hub objects
				       const availableClients = Array.isArray(response)
					       ? response.map((item) => ({
							       client_id: item.client_id,
							       client_name: item.name || `Client ${item.client_id}`,
							       client_short_name: item.short_name || '',
						     }))
					       : []

				       // Do not auto-select any client by default
				       let selectedClientId = this.state.selectedClientId
				       // Only keep current selection if it's still valid in the new client list
				       if (selectedClientId && !availableClients.find(c => c.client_id === selectedClientId)) {
					       selectedClientId = ''
				       }

				       this.setState({
					       availableClients,
					       selectedClientId,
					       loadingClients: false,
				       })

				       return selectedClientId
			       } catch (error) {
				       console.error('Failed to load available clients:', error)
				       // If a newer request has started, abort this one
				       if (thisRequestId !== AssignedViolations.loadClientsRequestId) {
					       return '';
				       }
				       this.setState({
					       availableClients: [],
					       loadingClients: false,
				       })
				       return ''
			       }
		       }

	// Track the current request to prevent race conditions for loadClientCounts
	static loadCountsRequestId = 0;

	// Loads per-client violation counts for the current stage so they can be shown
	// next to each partner in the client dropdown (mirrors the Contact Enrichment view).
	// Overrides carry a value the calling handler has only just set, since its setState
	// has not landed by the time we build the request.
	loadClientCounts = async (stageOverride, filterOverrides = {}) => {
		AssignedViolations.loadCountsRequestId++;
		const thisRequestId = AssignedViolations.loadCountsRequestId;
		try {
			const stage = stageOverride !== undefined ? stageOverride : getModel().assignedViolations.selectedStage
			const state = permissionToStage(stage)
			const userId = getModel().me.data.user_id
			const override = (key, current) => (filterOverrides[key] !== undefined ? filterOverrides[key] : current)

			// The counts have to be bounded by the same filters as the batch itself. Left
			// unfiltered they advertise work the queue will not hand out — a partner reads as
			// having violations waiting, and the Assign click comes back empty.
			const params = new URLSearchParams({ userId })
			const market = override('market', this.state.selectedMarket)
			const createdFrom = override('createdFrom', this.state.createdFrom)
			const createdTo = override('createdTo', this.state.createdTo)
			if (market) params.set('market', market)
			if (createdFrom) params.set('createdFrom', createdFrom)
			if (createdTo) params.set('createdTo', createdTo)

			const response = await requestAPI(
				`/violations/statecount/${state}?${params.toString()}`,
				'GET',
			)

			// A newer request has started — discard this (stale) response.
			if (thisRequestId !== AssignedViolations.loadCountsRequestId) {
				return
			}

			// The queue hands out one channel at a time — web, Facebook or Instagram, whichever
			// is picked — so keep every split and let render pick the one the channel is asking for.
			// Showing the combined total made a partner read as "2 available" while the web
			// queue it would actually search was empty.
			const channelCounts = (violations, lockedCount) => {
				const total = Number(violations) || 0
				const locked = Number(lockedCount) || 0
				return { violations: total, locked, available: Math.max(total - locked, 0) }
			}

			const clientCounts = Array.isArray(response)
				? response.reduce((acc, item) => {
						acc[item.client_id] = {
							web: channelCounts(item.web_violations, item.web_locked_count),
							facebook: channelCounts(item.facebook_violations, item.facebook_locked_count),
							instagram: channelCounts(item.instagram_violations, item.instagram_locked_count),
						}
						return acc
					}, {})
				: {}

			this.setState({ clientCounts })
		} catch (error) {
			console.error('Failed to load client violation counts:', error)
			if (thisRequestId !== AssignedViolations.loadCountsRequestId) {
				return
			}
			this.setState({ clientCounts: null })
		}
	}

	loadUsers = async () => {
		try {
			// Get users from API
			const users = await requestAPI('/users', 'GET')
			const availableUsers = Array.isArray(users) ? users : []
			this.setState({ users: availableUsers })
		} catch (error) {
			console.error('Failed to load users:', error)
			this.setState({ users: [] })
		}
	}

	handleClientChange = async (selectedClientId) => {
		this.setState({ selectedClientId })
		await this.performSearch(selectedClientId)
	}

	// The clients worth picking, labelled with what the selected channel would hand out.
	clientOptions = () => {
		const { availableClients, clientCounts, channel, selectedClientId } = this.state
		const options = [{ value: '', label: '-- Select a client --', searchText: '' }]
		availableClients.forEach(client => {
			const counts = clientCounts ? clientCounts[client.client_id] : null
			const channelCounts = counts ? counts[channel] : null
			const available = channelCounts ? channelCounts.available : 0
			const locked = channelCounts ? channelCounts.locked : 0
			// Nothing to hand out, so keep it out of the way — unless the counts have not
			// loaded (every client would read 0) or it is the current pick, which the
			// picker has to be able to show.
			if (clientCounts && available === 0 && client.client_id !== selectedClientId) {
				return
			}
			const lockedSuffix = locked > 0 ? `, ${locked} locked` : ''
			options.push({
				value: client.client_id,
				label: `${client.client_short_name} - ${client.client_name} (${available} available${lockedSuffix})`,
				searchText: `${client.client_short_name} ${client.client_name}`,
			})
		})
		return options
	}

	handleStageChange = async (e) => {
		const Model = getModel()
		if (Model.assignedViolations.selectedStage !== e.target.value) {
			const selectedStage = e.target.value
			await updateModel(model => {
				model.assignedViolations.selectedStage = selectedStage;
				model.proxyPermissionLevel = selectedStage;
				return model;
			});
			// Reload groups, clients and per-client counts for the new stage
			this.loadClientCounts(selectedStage);
			const newSelectedGroup = await this.loadAvailableGroups(selectedStage);
			const newSelectedClientId = await this.loadAvailableClients();
			this.setState({
				selectedGroup: newSelectedGroup || this.state.selectedGroup,
				selectedClientId: newSelectedClientId || this.state.selectedClientId
			});
			await this.performSearch(
				newSelectedClientId || this.state.selectedClientId,
				undefined,
				newSelectedGroup || this.state.selectedGroup,
			);
		}
	}

	handleGroupChange = async (e) => {
		const selectedGroup = e.target.value
		this.setState({ selectedGroup })
		await this.loadAvailableClients(selectedGroup)
		await this.performSearch(undefined, undefined, selectedGroup)
	}

	handleUserChange = async (e) => {
		const selectedUserId = e.target.value
		this.setState({ selectedUserId })
		await this.performSearch(undefined, selectedUserId)
	}

	handleAmountChange = (e) => {
		const value = e.target.value
		this.setState({ requestedAmount: value })
	}

	handleChannelChange = async (e) => {
		const channel = e.target.value
		this.setState({ channel })
		await this.performSearch(undefined, undefined, undefined, channel)
	}

	handleMarketChange = async (e) => {
		const selectedMarket = e.target.value
		this.setState({ selectedMarket })
		this.loadClientCounts(undefined, { market: selectedMarket })
		await this.performSearch(undefined, undefined, undefined, undefined, selectedMarket)
	}

	handleCreatedFromChange = async (e) => {
		const createdFrom = e.target.value
		this.setState({ createdFrom })
		this.loadClientCounts(undefined, { createdFrom })
		await this.performSearch(undefined, undefined, undefined, undefined, undefined, createdFrom)
	}

	handleCreatedToChange = async (e) => {
		const createdTo = e.target.value
		this.setState({ createdTo })
		this.loadClientCounts(undefined, { createdTo })
		await this.performSearch(undefined, undefined, undefined, undefined, undefined, undefined, createdTo)
	}

	performSearch = async (clientId, userId, group, channel, market, createdFrom, createdTo) => {
		const { selectedClientId, requestedAmount, selectedUserId, selectedGroup, channel: stateChannel, selectedMarket, createdFrom: stateCreatedFrom, createdTo: stateCreatedTo } = this.state
		const Model = getModel()
		const amount = parseInt(requestedAmount, 10)
		if (isNaN(amount) || amount <= 0) {
			alert('Please enter a valid number of violations (greater than 0)')
			return
		}
		const finalClientId = clientId !== undefined ? clientId : selectedClientId
		const finalGroup = group !== undefined ? group : selectedGroup

		// Don't search if no client or group is selected
		if (!finalClientId && !finalGroup) {
			initAssignedViolations()
			return
		}

		const finalUserId = userId !== undefined ? userId : selectedUserId
		const finalChannel = channel !== undefined ? channel : stateChannel
		const finalMarket = market !== undefined ? market : selectedMarket
		const finalCreatedFrom = createdFrom !== undefined ? createdFrom : stateCreatedFrom
		const finalCreatedTo = createdTo !== undefined ? createdTo : stateCreatedTo
		await initAssignedViolations()
		const result = await getAssignedViolations(finalClientId, finalGroup, getModel().me.data.user_id, getModel().assignedViolations.selectedStage, amount, finalUserId, finalChannel === 'instagram', finalChannel === 'facebook', finalMarket, finalCreatedFrom, finalCreatedTo)
		if (result && result.lockTimestamp) {
			this.startLockTimer(result.lockTimestamp)
		}
	}

	handleAmountKeyPress = async (e) => {
		if (e.key === 'Enter') {
			await this.performSearch()
		}
	}

	applyAllCachedActions = async () => {
		// Prevent multiple simultaneous executions
		if (this.state.isApplying) {
			return;
		}

		const { cachedActions } = this.state;
		const violationIds = Object.keys(cachedActions);

		if (violationIds.length === 0) {
			alert('No violations to apply');
			return;
		}

		const confirmed = window.confirm(`Are you sure you want to apply ${violationIds.length} violation(s)? This will make the results invisible after refreshing.\n\nPlease wait until the next popup appears before closing the page.`);
		if (!confirmed) return;

		this.setState({ isApplying: true });

		// Send each cached action. Failures are collected rather than alerted one by
		// one — a blocking dialog per violation made a batch of 20 unusable — and the
		// ones that failed keep their cached action so a retry doesn't lose the work.
		const failures = [];
		const appliedIds = [];

		for (const violationId of violationIds) {
			const cachedAction = cachedActions[violationId];

			try {
				const response = await setAction(
					violationId,
					{
						is_violating: cachedAction.isViolating,
					},
					getModel().proxyPermissionLevel,
					cachedAction.rejectReason
				);

				// A failed request resolves with the error body (or the raw Response)
				// rather than rejecting, so an absent updatedViolation is the real
				// signal that nothing was applied.
				const { newAction, updatedViolation } = response || {};
				if (!updatedViolation || !newAction) {
					failures.push({
						violationId,
						message: describeApplyFailure(response),
					});
					continue;
				}

				appliedIds.push(violationId);

				await updateModel(model => {
					model.assignedViolations.data.violations = model.assignedViolations.data.violations.map(violation => {
						if (violation.violation_id === violationId) {
							return ({
								...violation,
								...updatedViolation,
								actions: [...(violation.actions || []), newAction]
							});
						}
						return violation;
					});
					return model;
				});
			} catch (error) {
				console.error(`Error applying cached action for violation ${violationId}:`, error);
				failures.push({ violationId, message: describeApplyFailure(null, error) });
			}
		}

		// Keep the failed ones cached so the reviewer can retry without redoing them.
		const remainingActions = {};
		failures.forEach(({ violationId }) => {
			remainingActions[violationId] = cachedActions[violationId];
		});
		this.setState({ cachedActions: remainingActions, isApplying: false });

		// Label each failure with something the reviewer recognises on screen — the
		// domain and image name — rather than a bare UUID. Read before performSearch
		// replaces the list.
		const knownViolations = (getModel().assignedViolations.data.violations) || [];
		const labelled = failures.map(failure => ({
			...failure,
			label: describeViolation(knownViolations, failure.violationId),
		}));

		// Refresh the assigned violations using current state filters
		await this.performSearch()

		if (failures.length === 0) {
			alert(`All ${appliedIds.length} violation(s) have been successfully applied!`);
			return;
		}

		alert(summariseApplyFailures(appliedIds.length, violationIds.length, labelled));
	};

	render() {
		if (this.props.client_ids.length === 0) return <NoClientsSelected />
		const Model = getModel()
		const permission = Model.proxyPermissionLevel
		const email = Model.me.data.email
		const cachedCount = Object.keys(this.state.cachedActions).length
		const { availableClients, availableGroups, selectedClientId, loadingClients, loadingGroups, requestedAmount, selectedUserId, users, selectedGroup, channel, selectedMarket, createdFrom, createdTo, timeRemaining } = this.state
		const isRejectedState = Model.assignedViolations.selectedStage === 'rejected'
		
		return (
			<div id="assigned-violations">
				<h1 className="heading">
					{this.context.gettext('admin.violations.assigned_violations')} ({Model.assignedViolations.data.totalViolationNumber})
					<div className='d-flex'>
						{timeRemaining !== null && timeRemaining > 0 && (
							<span className={`lock-countdown${timeRemaining < 5 * 60 * 1000 ? ' lock-countdown--urgent' : ''}`}>
								Lock expires in: {this.formatTimeRemaining(timeRemaining)}
							</span>
						)}
						<select
							className="client-selector"
							value={selectedGroup}
							onChange={this.handleGroupChange}
							disabled={loadingGroups || availableGroups.length === 0}
							style={{ marginRight: '10px' }}
						>
							{loadingGroups ? (
								<option>Loading groups...</option>
							) : availableGroups.length === 0 ? (
								<option>No groups available</option>
							) : (
							<>
								<option value="">-- Select a group --</option>
								{availableGroups.map(group => (
									<option key={group} value={group}>
										{group}
									</option>
								))}
							</>
							)}
												</select>
						<ClientCombobox
							options={this.clientOptions()}
							value={selectedClientId}
							onChange={this.handleClientChange}
							disabled={loadingClients || availableClients.length === 0}
							placeholder={loadingClients ? 'Loading clients...' : availableClients.length === 0 ? 'No clients available' : '-- Select a client --'}
							emptyLabel="No clients with violations available"
						/>
						<select
							className="stage-selector"
							value={Model.assignedViolations.selectedStage}
							onChange={this.handleStageChange}
				>
					<option value="agent">Stage 1 (Agent)</option>
					<option value="admin">Stage 2 (Admin)</option>
					<option value="customer">Stage 3 (Client)</option>
					<option value="rejected">Rejected</option>
				</select>
						{isRejectedState && (
							<select
								className="user-selector"
								value={selectedUserId}
								onChange={this.handleUserChange}
								style={{ marginLeft: '10px' }}
							>
								<option value="">Select user who rejected...</option>
								{users.map(user => (
									<option key={user.user_id} value={user.user_id}>
										{user.email}
									</option>
								))}
							</select>
						)}
						<select
							className="market-selector"
							value={selectedMarket}
							onChange={this.handleMarketChange}
							style={{ marginLeft: '10px' }}
						>
							<option value="">All markets</option>
							<option value="unknown">Unknown</option>
							{markets.map(market => (
								<option key={market.code} value={market.code}>
									{market.fullName}
								</option>
							))}
						</select>
						<input
							type="date"
							className="created-from-input"
							value={createdFrom}
							onChange={this.handleCreatedFromChange}
							title="Only violations created on or after this date"
							style={{ marginLeft: '10px' }}
						/>
						<input
							type="date"
							className="created-to-input"
							value={createdTo}
							onChange={this.handleCreatedToChange}
							title="Only violations created on or before this date"
							style={{ marginLeft: '10px' }}
						/>
						<input
							type="number"
							className="amount-input"
						value={requestedAmount}
						onChange={this.handleAmountChange}
						onKeyPress={this.handleAmountKeyPress}
						min="1"
							placeholder="Amount"
							style={{ marginLeft: '10px', width: '80px' }}
						/>
						<div className="platform-toggle-group" role="radiogroup" aria-label="Channel" style={{ marginLeft: '10px' }}>
							{CHANNELS.map(({ value, label }) => (
								<label key={value} className="platform-toggle">
									<input
										type="radio"
										name="assigned-violations-channel"
										value={value}
										checked={channel === value}
										onChange={this.handleChannelChange}
									/>
									{label}
								</label>
							))}
						</div>
					</div>
				</h1>
				{Model.assignedViolations.data.violations.map(violation => (
					<div key={violation.violation_id} className="violation-card">
				<PhotoWithViolations
					key={violation.violation_id}
					violation={violation}
					proxyPermissionLevel={permission}
					email={email}
					markRemainingViolationsAsNotViolating={(role, reason) => {
						if (reason === 'blacklistDomain') {
							return markRemainingViolationsByDomain(violation.domain, role, reason)
						} else {
							return markRemainingViolationsByImageId(violation.image_id, role, reason)
						}
					}}
					onCachedActionChange={this.handleCachedActionChange}
				/>
					{Model.proxyPermissionLevel === 'agent' && (
						<div className='flex-center'>
						<Button onClick={() => {
							markRemainingViolationsByImageId(violation.image_id, Model.proxyPermissionLevel, "other");
						}}>
							Mark all remaining potential violations of the above image as not-violating
							</Button>
						</div>
						)}
					</div>
				))}
				{Model.assignedViolations.status.handle === 'loading' ? (
					<div className="loading-state">
						<h1>
							{this.context.gettext('admin.violations.searching_for_violations')}
						</h1>
					</div>
				) : (
					Model.assignedViolations.data.violations.length === 0 && (
						<div className="empty-state">
							<h1>
								{this.context.gettext('admin.violations.no_violations')}
							</h1>
						</div>
					)
				)}
                {Model.proxyPermissionLevel === 'customer' && cachedCount > 0 && (
                    <div className="apply-all-container">
                        <button className="apply-all-button" onClick={this.applyAllCachedActions}>
                            Apply All Violations ({cachedCount})
                        </button>
                    </div>
                )}
            </div>
		)
	}

	componentWillUnmount = () => {
		if (this.lockTimerInterval) {
			clearInterval(this.lockTimerInterval)
		}
	}

	componentDidMount = async () => {
		// Load users from API
		await this.loadUsers()
		
		// Load available clients but don't auto-select or load violations
		await this.loadAvailableClients()
		await this.loadAvailableGroups()

		// Load per-client violation counts for the current stage (shown in the client dropdown)
		this.loadClientCounts()
		
		// Initialize with no violations since no client is selected by default
		initAssignedViolations()
	}

	componentWillReceiveProps = nextProps => {
		if (nextProps.client_ids.length > 0) {
			if (nextProps.client_ids.join(',') !== this.props.client_ids.join(',')) {
				initAssignedViolations()
				getAssignedViolations(nextProps.client_ids[0], undefined, getModel().me.data.user_id)
			}
		}
	}
}

async function markRemainingViolationsByImageId(
	imageId,
	permissionLevel,
	rejectReasons,
) {
	const Model = getModel()
	const relatedToImage = violation => violation.image_id === imageId
	const violationsWithoutAction = Model.assignedViolations.data.violations.filter(v => relatedToImage(v))
	if (violationsWithoutAction.length === 0) return

	const results = await Promise.all(
		violationsWithoutAction.map(violation =>
			setAction(
				violation.violation_id,
				{ is_violating: false },
				permissionLevel,
				rejectReasons,
			),
		),
	)
	await updateModel(model => {
		results.forEach(result => {
			const { updatedViolation, newAction } = result
			const violation = violationsWithoutAction.find(violation => violation.violation_id === updatedViolation.violation_id)
			violation.actions = [...violation.actions, newAction]
		})
		return model
	})
}

async function markRemainingViolationsByDomain(
	domain,
	permissionLevel,
	rejectReasons,
) {
	const Model = getModel()
	const relatedToDomain = violation => violation.domain === domain
	const relatedViolations = Model.assignedViolations.data.violations.filter(v => relatedToDomain(v))
	if (relatedViolations.length === 0) return

	const results = await Promise.all(
		relatedViolations.map(violation =>
			setAction(
				violation.violation_id,
				{ is_violating: false },
				permissionLevel,
				rejectReasons,
			),
		),
	)
	await updateModel(model => {
		results.forEach(result => {
			const { updatedViolation, newAction } = result
			const violation = relatedViolations.find(violation => violation.violation_id === updatedViolation.violation_id)
			violation.actions = [...violation.actions, newAction]
		})
		return model
	})
}

AssignedViolations.contextTypes = {
	gettext: PropTypes.func.isRequired,
}

const CHANNELS = [
	{ value: 'web', label: 'Web' },
	{ value: 'facebook', label: 'Facebook' },
	{ value: 'instagram', label: 'Instagram' },
]

// Map permission level to stage value for API
const permissionToStage = (permission) => {
	const mapping = {
		'agent': 'stage_01',
		'admin': 'stage_02',
		'customer': 'stage_03',
		'rejected': 'rejected',
	}
	return mapping[permission] || 'stage_01'
}

// Track the current request to prevent race conditions
let currentRequestId = 0

const getAssignedViolations = (clientId, group, userId, state, amount = 20, rejectedByUserId = null, includeInstagram = false, includeFacebook = false, market = '', createdFrom = '', createdTo = '') => {
	const model = getModel()
	const selectedStage = state || model.assignedViolations.selectedStage

		// Increment request ID to invalidate previous requests
		currentRequestId++
		const thisRequestId = currentRequestId

		updateModel(model => {
			model.assignedViolations.status.handle = 'loading'
			return model
		})

		const requestBody = {
			...(clientId ? { clientId } : {}),
			...(group ? { group } : {}),
			userId: userId,
			state: permissionToStage(selectedStage),
			amount: amount,
			includeInstagram: includeInstagram,
			includeFacebook: includeFacebook,
			...(market ? { market } : {}),
			...(createdFrom ? { createdFrom } : {}),
			...(createdTo ? { createdTo } : {}),
		}

		// Add rejectedByUserId only if state is rejected and userId is provided
		if (selectedStage === 'rejected' && rejectedByUserId) {
			requestBody.rejectedByUserId = rejectedByUserId
		}
		
		return requestAPI(
			`/violations/assign`,
			'POST',
			requestBody
		).then(violations => {
			// Only update model if this is still the current request
			if (thisRequestId !== currentRequestId) {
				return // Ignore outdated response
			}

			updateModel(model => {
				// Ensure all violations have an actions array
				const normalizedViolations = violations.map(v => ({
					...v,
					actions: v.actions || []
				}))
				const numberOfViolations = normalizedViolations.length
				// Show all violations immediately
				model.assignedViolations.data.violations = normalizedViolations
				model.assignedViolations.status.violationsNotShown = []
				model.assignedViolations.data.totalViolationNumber = numberOfViolations
				model.assignedViolations.status.handle = 'loaded'
				model.assignedViolations.status.timestamp = new Date()
				return model
			})

			const lockTimestamp = violations.length > 0 ? violations[0].last_assignment_date : null
			return { lockTimestamp }
		})
}

function initAssignedViolations() {
	updateModel(model => {
		model.assignedViolations.data.violations = []
		return model
	})
}

export default AssignedViolations
